import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getIn } from 'formik';
import { useCallback, useMemo } from 'react';
import { generatePath } from 'react-router';
import { toast } from 'react-toastify';
import { ConfirmEvent, useModal } from 'yoso';
import { AdventureBottomSheet } from '~/components/Adventure/BottomSheet/AdventureBottomSheet';
import { JoinAdventureValues } from '~/components/Adventure/Form/AdventureForm.hooks';
import { AdventureSuccessPopup } from '~/components/Adventure/Popup/AdventureSuccessPopup/AdventureSuccessPopup';
import { InsufficientPopup } from '~/components/Adventure/Popup/InsufficientPopup/InsufficientPopup';
import { NftRequiredPopup } from '~/components/Adventure/Popup/NftRequiredPopup';
import { useSmsVerification } from '~/components/Auth';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { ContentModel, toObsContent } from '~/components/Content/Content.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { useDefaultErrorHandler } from '~/components/Error/Error.hooks';
import { HttpError } from '~/components/Error/HttpError';
import { t } from '~/components/I18n/i18n';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useTransition } from '~/components/Security';
import { useNow } from '~/components/Util';

export type Adventure = {
  id: number;
  type: 'WEEKLY' | 'UNKNOWN';
  participation: {
    overallCount: number;
    currentCount: number;
    maxCount: number;
    assetType: AssetType;
    priceList: AdventurePrice[];
  };
  milestoneList: Milestone[];
  rewardList: AdventureReward[];
  startAt: Date;
  endAt: Date;
  announcedAt: Date;
  status: 'UPCOMING' | 'IN_PROGRESS' | 'END';
};

export type AssetType = 'DON' | 'LUP';

export type RewardType = 'CITIZEN';

export type AdventureReward = {
  id: number;
  title: string;
  owner: string;
  rewardType: 'LOTTERY_NFT' | 'PARTICIPATORY_NFT' | 'UNKNOWN';
  description: string;
  winnerCount: number;
  requiredCount: number;
  allowedRegion: 'ALL' | 'GLOBAL' | 'JP';
  adventureId: number;
} & ContentModel;

export type AdventurePrice = {
  count: number;
  assetType: AssetType;
  amount: number;
};

export type Milestone = {
  count: number;
  rewardType: RewardType;
  amount: number;
  level: number;
};

export type AdventureResponse = Omit<Adventure, 'startAt' | 'endAt'> & {
  startAt: number;
  endAt: number;
};

export const adventuresUrl = '/api/citizen/v1/adventures';
export const adventureUrl = '/api/citizen/v1/adventures/:id';

export const transformAdventureReward = (data: AdventureReward) => ({
  ...data,
  ...toObsContent(data),
});

export const transformAdventureData: (data: AdventureResponse) => Adventure = (data) => ({
  ...data,
  rewardList: data.rewardList?.map(transformAdventureReward).map((reward) => ({
    ...reward,
    adventureId: data.id,
  })),
  startAt: new Date(data.startAt),
  endAt: new Date(data.endAt),
  announcedAt: new Date(data.announcedAt),
});

export const getAdventure = async (id: Adventure['id']) => {
  const { data } = await axios.get<AdventureResponse>(generatePath(adventureUrl, { id: id }));
  return transformAdventureData(data);
};

export const useAdventure = (id: Adventure['id'], options?: UseDosiminQueryOptions<Adventure>) =>
  useQuery<Adventure>(['adventure', id], () => getAdventure(id), options);

export const useSyncAdventureStatus = (
  id: Adventure['id'],
  options?: UseDosiminQueryOptions<Adventure>
) => {
  const now = useNow();
  const { data: { startAt, endAt } = {} } = useAdventure(id, options);
  const syncStatus = useMemo(() => {
    if (!startAt || !endAt) {
      return null;
    }

    if (now < startAt) {
      return 'UPCOMING';
    } else if (now >= startAt && now < endAt) {
      return 'IN_PROGRESS';
    } else {
      return 'END';
    }
  }, [endAt, now, startAt]);

  return syncStatus;
};

export const useAdventureNextPrice = (id: Adventure['id']) => {
  const { data } = useAdventure(id);
  const priceList = getIn(data, 'participation.priceList', []);
  const currentCount = getIn(data, 'participation.currentCount', 0);
  return priceList[currentCount]?.amount || 0;
};

export const getAdventures = async () => {
  const {
    data: { adventureList },
  } = await axios.get<{ adventureList: AdventureResponse[] }>(adventuresUrl);

  if (adventureList.length === 0) {
    throw new BaseError('EMPTY');
  }

  return adventureList.map<Adventure>(transformAdventureData);
};

export const useAdventures = (options?: UseDosiminQueryOptions<Adventure[]>) =>
  useQuery<Adventure[]>(['adventure'], getAdventures, options);

export const useFeaturedAdventure = (options?: UseDosiminQueryOptions<Adventure[]>) => {
  const { data: [adventure] = [] } = useAdventures(options);
  const { id } = adventure || {};
  return useAdventure(id, { enabled: !!id });
};

export type JoinAdventureError = HttpError<{
  responseData: {
    neededAssetType: 'DON' | 'LUP';
    neededAmount: number;
    balance: number;
  };
}>;
export const useJoinAdventureErrorHandler = () => {
  const { open } = useModal();
  const client = useQueryClient();
  const defaultErrorHandler = useDefaultErrorHandler();
  return useCallback(
    async (error: JoinAdventureError) => {
      const { neededAmount = 0, balance = 0 } = error.payload?.response?.data?.responseData || {};
      switch (error.message) {
        case 'JOIN_ADVENTURE_INSUFFICIENT_LUP':
          await open(<InsufficientPopup required={neededAmount} balance={balance} />);
          client.invalidateQueries(['adventure']);
          break;
        case 'NFT_REQUIRED':
          await open(<NftRequiredPopup />);
          break;
        case 'JOIN_ADVENTURE_TIME_OVER':
          toast(t('citizen_closed_adventure_toast'));
          break;
        case 'JOIN_ADVENTURE_COUNT_EXCEED':
        default:
          defaultErrorHandler();
          break;
      }
    },
    [client, defaultErrorHandler, open]
  );
};

export const joinAdventureUrl = '/api/citizen/v1/adventures/:id/participation';

export type AdventureStatus = {
  id: Adventure['id'];
  currentCount: number;
  maxCount: number;
};

export type JoinAdventureParams = {
  id: Adventure['id'];
  count: number;
};

export const joinAdventure = async ({ id, count }: JoinAdventureParams) => {
  const url = generatePath(joinAdventureUrl, { id });
  const { data } = await axios.post<AdventureStatus>(url, { count });
  return { ...data, id };
};

export const useJoinAdventure = (
  options?: UseDosiminMutationOptions<AdventureStatus, JoinAdventureError, JoinAdventureParams>
) => {
  const onError = useJoinAdventureErrorHandler();
  return useMutation<AdventureStatus, JoinAdventureError, JoinAdventureParams>({
    mutationFn: (params: JoinAdventureParams) => joinAdventure(params),
    onError,
    ...options,
  });
};

const CITIZEN_STORE_URL = import.meta.env.VITE_CITIZEN_STORE_URL;
export const useProceedJoinAdventureSuccessHandler = () => {
  const queryClient = useQueryClient();
  const modal = useModal();
  return useCallback(
    async ({ id, currentCount, maxCount }: AdventureStatus) => {
      queryClient.invalidateQueries(['adventure', id]);
      const { confirmed } = await modal.open(
        <AdventureSuccessPopup id={id} currentCount={currentCount} maxCount={maxCount} />
      );
      if (confirmed) location.href = `${CITIZEN_STORE_URL}/marketplace`;
    },
    [modal, queryClient]
  );
};

export const useProceedJoinAdventure = (
  options?: UseDosiminMutationOptions<AdventureStatus, BaseError, Adventure['id']>
) => {
  const [opened, proceed] = useTransition<ConfirmEvent<JoinAdventureValues>>();
  const bottomSheet = useBottomSheet();
  const { mutateAsync: verify, isSuccess } = useSmsVerification();
  const { mutateAsync: joinAdventure } = useJoinAdventure();
  const onSuccess = useProceedJoinAdventureSuccessHandler();
  const mutation = useMutation({
    mutationFn: async (id) => {
      if (!isSuccess) {
        await verify();
      }

      const { confirmed, ...values } = await proceed(
        () =>
          bottomSheet.open(<AdventureBottomSheet id={id} />, { throwOnDismiss: true }) as Promise<
            ConfirmEvent<JoinAdventureValues>
          >
      );
      const params = { ...values, id };

      return await joinAdventure(params);
    },
    onSuccess,
    onError: undefined,
    ...options,
  });

  return {
    ...mutation,
    opened,
  };
};

export const usePossibleAdventureCount = (id: number) => {
  const {
    data: { amount: donBalance },
  } = useBalance();
  const { data: adventure } = useAdventure(id);
  const { participation } = adventure || {};
  const { priceList = [], currentCount = 0, maxCount = 100 } = participation || {};

  const possibleAdventureCount = useMemo(() => {
    let balance = donBalance;
    let accumulator = 0;
    for (let i = currentCount; i < maxCount; i++) {
      const requiredAmount = priceList[currentCount]?.amount;
      if (requiredAmount === undefined) break;
      if (balance < requiredAmount) break;
      balance -= requiredAmount;
      accumulator += 1;
    }
    return accumulator;
  }, [currentCount, donBalance, maxCount, priceList]);

  return { possibleAdventureCount };
};
